// Система аутентификации пользователей
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'doctor' | 'nurse' | 'admin';
  specialization?: string;
  hospital?: string;
  createdAt: Date;
  lastLogin: Date;
  isActive: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  role: 'doctor' | 'nurse';
  specialization?: string;
  hospital?: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Менеджер аутентификации
export class AuthManager {
  private static instance: AuthManager;
  private currentUser: User | null = null;
  private listeners: ((state: AuthState) => void)[] = [];

  private constructor() {
    this.loadUserFromStorage();
  }

  public static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  // Подписка на изменения состояния аутентификации
  public subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Уведомление слушателей об изменениях
  private notifyListeners(): void {
    const state: AuthState = {
      user: this.currentUser,
      isAuthenticated: !!this.currentUser,
      isLoading: false,
      error: null
    };
    this.listeners.forEach(listener => listener(state));
  }

  // Загрузка пользователя из localStorage
  private loadUserFromStorage(): void {
    try {
      const storedUser = localStorage.getItem('pediatrics_user');
      if (storedUser) {
        this.currentUser = JSON.parse(storedUser);
        this.notifyListeners();
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователя:', error);
      this.logout();
    }
  }

  // Сохранение пользователя в localStorage
  private saveUserToStorage(user: User): void {
    try {
      localStorage.setItem('pediatrics_user', JSON.stringify(user));
    } catch (error) {
      console.error('Ошибка сохранения пользователя:', error);
    }
  }

  // Регистрация нового пользователя
  public async register(data: RegisterData): Promise<{ success: boolean; error?: string }> {
    try {
      // Проверка существующего пользователя
      const existingUsers = this.getStoredUsers();
      if (existingUsers.find(u => u.email === data.email)) {
        return { success: false, error: 'Пользователь с таким email уже существует' };
      }

      // Создание нового пользователя
      const newUser: User = {
        id: this.generateId(),
        email: data.email,
        name: data.name,
        role: data.role,
        specialization: data.specialization,
        hospital: data.hospital,
        createdAt: new Date(),
        lastLogin: new Date(),
        isActive: true
      };

      // Сохранение пользователя
      this.saveUser(newUser);
      this.currentUser = newUser;
      this.saveUserToStorage(newUser);
      this.notifyListeners();

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Ошибка регистрации пользователя' };
    }
  }

  // Вход в систему
  public async login(credentials: LoginCredentials): Promise<{ success: boolean; error?: string }> {
    try {
      const users = this.getStoredUsers();
      const user = users.find(u => u.email === credentials.email && u.isActive);
      
      if (!user) {
        return { success: false, error: 'Пользователь не найден' };
      }

      // В реальном приложении здесь была бы проверка пароля
      // Для демонстрации принимаем любой пароль
      if (credentials.password.length < 6) {
        return { success: false, error: 'Пароль должен содержать минимум 6 символов' };
      }

      // Обновление времени последнего входа
      user.lastLogin = new Date();
      this.updateUser(user);
      
      this.currentUser = user;
      this.saveUserToStorage(user);
      this.notifyListeners();

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Ошибка входа в систему' };
    }
  }

  // Выход из системы
  public logout(): void {
    this.currentUser = null;
    localStorage.removeItem('pediatrics_user');
    this.notifyListeners();
  }

  // Получение текущего пользователя
  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  // Проверка аутентификации
  public isAuthenticated(): boolean {
    return !!this.currentUser;
  }

  // Получение сохраненных пользователей
  private getStoredUsers(): User[] {
    try {
      const stored = localStorage.getItem('pediatrics_users');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  }

  // Сохранение пользователя в список
  private saveUser(user: User): void {
    const users = this.getStoredUsers();
    const existingIndex = users.findIndex(u => u.id === user.id);
    
    if (existingIndex >= 0) {
      users[existingIndex] = user;
    } else {
      users.push(user);
    }
    
    localStorage.setItem('pediatrics_users', JSON.stringify(users));
  }

  // Обновление пользователя
  private updateUser(user: User): void {
    this.saveUser(user);
  }

  // Генерация ID
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Получение статистики пользователей
  public getUsersStats(): {
    totalUsers: number;
    activeUsers: number;
    doctors: number;
    nurses: number;
  } {
    const users = this.getStoredUsers();
    return {
      totalUsers: users.length,
      activeUsers: users.filter(u => u.isActive).length,
      doctors: users.filter(u => u.role === 'doctor').length,
      nurses: users.filter(u => u.role === 'nurse').length
    };
  }
}

// Экспорт глобального экземпляра
export const authManager = AuthManager.getInstance();
